/**
 * `<caos-summary-panel>` — a generated summary where every number traces back to the field it came from.
 * (Catalog: AI capabilities › SummaryPanel — the Summarize capability's binding: prose whose claims carry fieldRef citations back into the record.)
 *
 * CAOS-158. Summarize is the one capability that answers in prose, and prose is the one output a
 * contract cannot check. `ai-platform.md` says so in terms: the `text` contract treats the string as
 * the instance and never parses it, so **citations are not enforced today**, and "a surface that
 * needs cited summaries has to establish the citations itself until a contract family enforces
 * them." This is that surface. The enforcement lives here because that is where the architecture
 * puts it, not because the kernel was the harder place to change.
 *
 * ── CITATIONS ARRIVE AS MARKERS, NOT AS OFFSETS ─────────────────────────────────────────────────
 * Each citation names a marker token — `[1]`, `[rev]` — that appears in the prose, plus the object,
 * field and record it points at. The alternative design, a character offset into the prose, was
 * rejected: an offset is correct only against the exact string the model emitted, so any trimming,
 * re-wrapping or truncation downstream silently slides every citation onto the wrong words, and
 * nothing fails. A marker travels with the sentence it belongs to. It is also something a prompt can
 * be instructed to produce and an eval case can assert, which an offset is not.
 *
 * ── WHAT "UNCITED" MEANS HERE, AND WHY IT IS A MECHANICAL RULE ──────────────────────────────────
 * The architecture's intent is that an uncited assertion is a violation. This component cannot
 * decide what counts as an assertion — that is a judgement about language — so it does not pretend
 * to. It applies one rule that can be checked instead of trusted: **a sentence containing a digit is
 * a quantitative claim, and a quantitative claim carrying no marker that RESOLVES to a supplied
 * citation is marked.** Resolution, not bracket shape: "Revenue grew 12% [unverified]." is uncited,
 * because no citation names `unverified` and the figure therefore traces to nothing.
 *
 * That is deliberately narrower than the intent. It will not catch "the account is at risk", which
 * is an uncited assertion too. It also will not cry wolf, which is the failure that gets a check
 * switched off — and every number in a summary is exactly the thing somebody would otherwise
 * retype into a report and defend in a meeting. {@link uncited} is exposed so a surface can refuse
 * to publish a summary rather than merely show the marks, which is the difference between a warning
 * and a gate.
 *
 * ── A CITATION NOBODY CAN SEE IS ALSO A FINDING ─────────────────────────────────────────────────
 * A citation whose marker never appears in the prose is reported through {@link orphaned} rather
 * than dropped. Dropping it is how a summary ends up looking fully cited while half its citations
 * point at nothing: the list is long, the prose is clean, and the two were never compared.
 *
 * ── AN EMPTY SUMMARY IS A REFUSAL, NOT A BLANK PANEL ────────────────────────────────────────────
 * The `text` contract already reports an empty or whitespace-only body as `empty_output`, on the
 * stated grounds that an empty answer indistinguishable from "nothing to say" is worse than a
 * refusal. The panel honours the same judgement on screen: no prose draws the refusal line, never an
 * empty box that reads as still loading.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────────────────────────
 * It does not invoke anything, and it does not open the record. Pressing a citation raises
 * `caos-summary-cite`; the application opens the field wherever records open, which is what keeps
 * this usable by an application whose records live somewhere this library has never heard of.
 *
 * Styling seams: `::part(summary)`, `::part(prose)`, `::part(sentence)`, `::part(citation)`,
 * `::part(uncited)`, `::part(verdict)`, `::part(refusal)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/**
 * One citation: a marker in the prose, and the field it points back at.
 *
 * `object` and `field` are the `fieldRef` the architecture asks for. `recordId` is separate because
 * a summary spans a record AND its related lists, so two citations can name the same field of
 * different records and a reader needs to land on the right one.
 */
export interface CaosSummaryCitation {
  /** The token as it appears in the prose, WITHOUT its brackets — `1`, `rev`, `total`. */
  readonly marker: string;
  /** The object the cited field belongs to. */
  readonly object: string;
  /** The field the claim came from. */
  readonly field: string;
  /** The record the value was read from. Absent ⇒ the surface's own record. */
  readonly recordId?: string;
  /** The value as it was read, shown on hover so a reader can check without leaving. */
  readonly value?: string;
}

/** A marker token in the prose: `[1]`, `[rev]`. Bounded so an unclosed bracket cannot swallow a paragraph. */
const MARKER = /\[([^\][\s]{1,24})\]/g;

/** Sentence boundaries. Crude on purpose — see {@link CaosSummaryPanel.uncited}. */
const SENTENCE = /[^.!?]+[.!?]*\s*/g;

/**
 * Split the body into runs whose concatenation is the body again, EXACTLY.
 *
 * {@link SENTENCE} needs a match to begin with a non-terminator, so a run of terminators that no
 * word precedes — an ellipsis, a stray `?` after a space — cannot start a match and is stepped over
 * by `matchAll`. Rendering only the matches would then DROP those characters from the screen, and a
 * panel whose whole purpose is to show what a model said would be showing something else: "Open
 * since 2019. ... needs follow-up." would draw without its ellipsis and nothing would fail.
 *
 * So the gaps are carried as runs of their own. They are ordinary text, they are never citation
 * controls, and they cannot be flagged as uncited claims because a run of punctuation holds no
 * digit. `summary-panel`'s test asserts the join-back invariant rather than trusting this comment.
 */
function sentenceRuns(body: string): readonly string[] {
  const runs: string[] = [];
  let cursor = 0;
  for (const match of body.matchAll(SENTENCE)) {
    const at = match.index ?? 0;
    if (at > cursor) runs.push(body.slice(cursor, at));
    runs.push(match[0]);
    cursor = at + match[0].length;
  }
  if (cursor < body.length) runs.push(body.slice(cursor));
  // A body with no sentence punctuation at all still has to draw, as one run.
  return runs.length > 0 ? runs : [body];
}

const sheet = makeSheet(`
  :host { display: block; }

  .summary { display: flex; flex-direction: column; gap: var(--caos-space-2); }

  .prose {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 14px;
    font-weight: var(--caos-font-weight-body);
    line-height: 1.65;
    color: var(--caos-color-text);
  }

  .sentence { display: inline; }

  .sentence.uncited {
    background: var(--caos-color-warning-bg);
    box-shadow: inset 0 -1px 0 var(--caos-color-warning);
  }

  .citation {
    display: inline;
    border: 0;
    padding: 0 2px;
    margin: 0 1px;
    background: none;
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 600;
    color: var(--caos-color-info-text);
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    vertical-align: super;
    line-height: 1;
  }
  .citation:hover { color: var(--caos-color-text); }
  .citation:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }

  .verdict {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 12px;
    color: var(--caos-color-text-muted);
  }
  .verdict:empty { display: none; }
  .verdict.short { color: var(--caos-color-warning-text); font-weight: 600; }

  .refusal {
    margin: 0;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface-2);
    padding: var(--caos-space-3);
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

export class CaosSummaryPanel extends CaosElement {
  static get observedAttributes(): string[] {
    return ['prose', 'refusal-text', 'uncited-label'];
  }

  private proseEl: HTMLParagraphElement;
  private verdictEl: HTMLParagraphElement;
  private refusalEl: HTMLParagraphElement;

  private cites: readonly CaosSummaryCitation[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'summary';
    wrap.setAttribute('part', 'summary');

    this.proseEl = document.createElement('p');
    this.proseEl.className = 'prose';
    this.proseEl.setAttribute('part', 'prose');

    this.verdictEl = document.createElement('p');
    this.verdictEl.className = 'verdict';
    this.verdictEl.setAttribute('part', 'verdict');
    this.verdictEl.setAttribute('role', 'status');

    this.refusalEl = document.createElement('p');
    this.refusalEl.className = 'refusal';
    this.refusalEl.setAttribute('part', 'refusal');

    wrap.append(this.refusalEl, this.proseEl, this.verdictEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get citations(): readonly CaosSummaryCitation[] {
    return this.cites;
  }

  set citations(next: readonly CaosSummaryCitation[]) {
    this.cites = (Array.isArray(next) ? next : []).filter(
      (c): c is CaosSummaryCitation =>
        c !== null &&
        typeof c === 'object' &&
        typeof c.marker === 'string' &&
        c.marker !== '' &&
        typeof c.object === 'string' &&
        typeof c.field === 'string',
    );
    this.render();
  }

  /** The summary body, trimmed. Empty ⇒ the `empty_output` case, drawn as a refusal. */
  get prose(): string {
    return (this.getAttribute('prose') ?? '').trim();
  }

  /** True when there is nothing to show — the contract's `empty_output`, on screen. */
  get empty(): boolean {
    return this.prose === '';
  }

  /**
   * The sentences carrying a number and no citation.
   *
   * The rule, stated so it can be argued with: a sentence is a run of text up to `.`, `!` or `?`; a
   * sentence with a digit in it is a quantitative claim; a quantitative claim containing no marker
   * token THAT RESOLVES TO A SUPPLIED CITATION is uncited. Read it to gate publishing a summary,
   * not merely to colour it.
   *
   * The resolving half is the whole guard. Testing only for bracket SHAPE would let
   * "Revenue grew 12% [unverified]." pass as cited: the aside is marker-shaped, no citation names
   * `unverified`, the panel correctly draws it as plain text — and the verdict line would still
   * have said every claim was cited. A number that traces to nothing, reported as traceable, is
   * precisely the outcome this component exists to prevent.
   */
  get uncited(): readonly string[] {
    const body = this.prose;
    if (body === '') return [];
    const byMarker = this.markerIndex();
    return sentenceRuns(body)
      .map((run) => run.trim())
      .filter((s) => s !== '' && /\d/.test(s) && !isCited(s, byMarker));
  }

  /** The supplied citations, by marker. Built per read so a late `citations` set is honoured. */
  private markerIndex(): Map<string, CaosSummaryCitation> {
    return new Map(this.cites.map((c) => [c.marker, c]));
  }

  /** Citations whose marker appears nowhere in the prose, so a reader can never reach them. */
  get orphaned(): readonly CaosSummaryCitation[] {
    const present = new Set([...this.prose.matchAll(MARKER)].map((m) => m[1]));
    return this.cites.filter((c) => !present.has(c.marker));
  }

  /** True when every quantitative claim carries a citation and every citation is reachable. */
  get fullyCited(): boolean {
    return !this.empty && this.uncited.length === 0 && this.orphaned.length === 0;
  }

  private render(): void {
    const body = this.prose;

    if (body === '') {
      this.refusalEl.hidden = false;
      this.refusalEl.textContent =
        this.getAttribute('refusal-text') ?? 'No summary was produced. An empty answer is reported rather than shown.';
      this.proseEl.hidden = true;
      this.verdictEl.hidden = true;
      this.proseEl.replaceChildren();
      return;
    }

    this.refusalEl.hidden = true;
    this.proseEl.hidden = false;
    this.verdictEl.hidden = false;

    const byMarker = this.markerIndex();
    const runs = sentenceRuns(body);

    this.proseEl.replaceChildren(
      ...runs.map((run) => {
        const span = document.createElement('span');
        span.className = 'sentence';
        span.setAttribute('part', 'sentence');
        const flagged = /\d/.test(run) && !isCited(run, byMarker);
        if (flagged) {
          span.classList.add('uncited');
          span.setAttribute('part', 'sentence uncited');
          // Marked in TEXT as well as in the tint: a highlight is not a word, and a reader on a
          // monochrome display or with a colour deficiency would otherwise see nothing at all.
          span.setAttribute('aria-label', `${this.getAttribute('uncited-label') ?? 'Uncited claim'}: ${run.trim()}`);
        }
        span.append(...this.draw(run, byMarker));
        return span;
      }),
    );

    const uncitedCount = this.uncited.length;
    const orphanCount = this.orphaned.length;
    this.verdictEl.classList.toggle('short', uncitedCount > 0 || orphanCount > 0);
    this.verdictEl.textContent = verdict(this.cites.length, uncitedCount, orphanCount);
  }

  /** Split one sentence into text and citation buttons. */
  private draw(run: string, byMarker: Map<string, CaosSummaryCitation>): Node[] {
    const out: Node[] = [];
    let cursor = 0;
    for (const match of run.matchAll(MARKER)) {
      const at = match.index ?? 0;
      const marker = match[1];
      const cite = marker === undefined ? undefined : byMarker.get(marker);
      if (cite === undefined) continue; // a bracketed word that is not a citation stays as written
      if (at > cursor) out.push(document.createTextNode(run.slice(cursor, at)));
      out.push(this.button(cite));
      cursor = at + match[0].length;
    }
    if (cursor < run.length) out.push(document.createTextNode(run.slice(cursor)));
    return out;
  }

  private button(cite: CaosSummaryCitation): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'citation';
    b.setAttribute('part', 'citation');
    b.textContent = `[${cite.marker}]`;
    const where = cite.value === undefined ? `${cite.object}.${cite.field}` : `${cite.object}.${cite.field} — ${cite.value}`;
    b.title = where;
    b.setAttribute('aria-label', `Open the field this came from: ${where}`);
    b.addEventListener('click', () => {
      this.dispatchEvent(
        new CustomEvent('caos-summary-cite', {
          bubbles: true,
          composed: true,
          detail: { object: cite.object, field: cite.field, recordId: cite.recordId ?? null, marker: cite.marker },
        }),
      );
    });
    return b;
  }
}

/**
 * Whether a run of text carries at least one marker that RESOLVES to a supplied citation.
 *
 * Shape alone is not enough, and the difference is the guard — see {@link CaosSummaryPanel.uncited}.
 * A fresh regex per call rather than the shared global one, because a global's `lastIndex` survives
 * a `test` and the next call would resume mid-string and answer about the wrong part of it.
 */
function isCited(run: string, byMarker: ReadonlyMap<string, CaosSummaryCitation>): boolean {
  for (const match of run.matchAll(new RegExp(MARKER.source, 'g'))) {
    const marker = match[1];
    if (marker !== undefined && byMarker.has(marker)) return true;
  }
  return false;
}

/** The one line under the prose, saying what is and is not traceable. */
function verdict(citations: number, uncited: number, orphaned: number): string {
  const parts: string[] = [];
  if (uncited > 0) parts.push(`${uncited} ${uncited === 1 ? 'claim carries' : 'claims carry'} no citation`);
  if (orphaned > 0) parts.push(`${orphaned} ${orphaned === 1 ? 'citation points' : 'citations point'} at nothing in the text`);
  if (parts.length > 0) return parts.join('; ');
  if (citations === 0) return 'Nothing in this summary states a number.';
  return `Every claim is cited — ${citations} ${citations === 1 ? 'citation' : 'citations'}, each opening the field it came from.`;
}

define('caos-summary-panel', CaosSummaryPanel);

declare global {
  interface HTMLElementTagNameMap {
    'caos-summary-panel': CaosSummaryPanel;
  }
}
